import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    private static readonly GRAPH_SCOPES = ['User.Read', 'GroupMember.Read.All'];

    constructor(private http: HttpClient, private msalService: MsalService) { }

    private async getGraphToken(): Promise<string> {
        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        if (!account) {
            return '';
        }

        try {
            const result = await this.msalService.instance.acquireTokenSilent({
                account,
                scopes: GraphService.GRAPH_SCOPES
            });
            return result.accessToken;
        } catch (error) {
            // Silent acquisition can fail when Graph consent has not been granted.
            if (error instanceof InteractionRequiredAuthError) {
                try {
                    const result = await this.msalService.instance.acquireTokenPopup({
                        account,
                        scopes: GraphService.GRAPH_SCOPES
                    });
                    return result.accessToken;
                } catch {
                    return '';
                }
            }
            return '';
        }
    }

    public async getUserPhoto(): Promise<string> {
        try {
            const token = await this.getGraphToken();

            if (!token) {
                return '';
            }

            const url = 'https://graph.microsoft.com/v1.0/me/photo/$value';
            const headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${token}`
            });

            const response = await lastValueFrom(
                this.http.get(url, { headers, responseType: 'blob' })
            );

            if (!response) {
                return '';
            }

            return await new Promise<string>((resolve) => {
                const reader: FileReader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string) || '');
                reader.onerror = () => resolve('');
                reader.readAsDataURL(response as Blob);
            });
        } catch {
            return '';
        }
    }

    public async getUserGroups(role: any): Promise<any> {
        let results: any = null;

        try {
            const token = await this.getGraphToken();
            let url = `https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=startswith(displayName, '${role}')`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${token}`,
                ConsistencyLevel: "eventual"
            });
            let stream$ = this.http.get(url, { headers: headers });

            results = await lastValueFrom(stream$);
        } catch (error: any) {
            throw error;
        }

        return results;
    }

    public async getUserMemberGroups(): Promise<any> {
        let results: any = null;

        try {
            const token = await this.getGraphToken();
            const headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${token}`,
                ConsistencyLevel: "eventual"
            });
            let url: string | null = 'https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$select=id,displayName';
            const groups: any[] = [];

            while (url) {
                results = await lastValueFrom(this.http.get<any>(url, { headers }));
                groups.push(...(results.value ?? []));
                url = results['@odata.nextLink'] ?? null;
            }

            results = { value: groups };
        } catch (error: any) {
            throw error;
        }

        return results;
    }
}
